import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LabelWithHelp from '../../../components/LabelWithHelp';
import { useGetAdminGroupsQuery } from '../../../api/group/groupAdminApiSlice';
import { useTenantCapabilities } from '../../../hooks/useTenantCapabilities';
import { selectableGroupsForServices } from './selectableGroups';
import { stationDayEnd } from '../../../utils/stationTime';
import { ALL_OBJECTS_SCOPE } from './codeScope';

// The terms every admin-created code carries — which objects it applies to, the
// discount and the expiry. Shared by the single-code form (`CreateCodeModal`,
// promo + goodwill) and the campaign form (`CreateCampaignModal`, #2249), so the
// two cannot drift. The backend holds the authoritative checks in
// `new-backend/admin/src/lib/voucher/codeTerms.ts`; `codeTermsValid` below only
// explains them earlier (ADR-0020).

export const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

// The "all objects" sentinel and the default scope of a new code live in
// `codeScope.ts` (a pure module, so they can be tested without the API layer).
export { ALL_OBJECTS_SCOPE } from './codeScope';

// The date input yields a local 'yyyy-mm-dd'; redemption rejects a voucher once
// valid_until_date < now. Store end-of-day so a code picked "valid until today"
// stays redeemable through that whole day instead of expiring at midnight.
// The end of the picked day on the station's clock (ADR-0019), not the browser's.
export const endOfDayIso = (dateStr: string) => stationDayEnd(dateStr).toISOString();

// Local calendar today (yyyy-mm-dd) — the earliest selectable expiry. Stops the
// user picking a past date that would create an already-expired code.
export const localToday = (): string => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export interface CodeTermsState {
    groupId: string;
    isPercentage: boolean;
    value: string;
    validUntil: string;
}

// Validation derives from form state (not API data), per the edit-form rule.
export const codeTermsValid = (terms: CodeTermsState, today: string): boolean => {
    const numValue = Number(terms.value);
    return (
        terms.groupId.trim().length > 0 &&
        Number.isFinite(numValue) &&
        numValue > 0 &&
        (!terms.isPercentage || numValue <= 100) &&
        terms.validUntil.length > 0 &&
        terms.validUntil >= today
    );
};

interface Props extends CodeTermsState {
    /** Groups are fetched only while the surrounding modal is open. */
    open: boolean;
    today: string;
    onGroupIdChange: (value: string) => void;
    onIsPercentageChange: (value: boolean) => void;
    onValueChange: (value: string) => void;
    onValidUntilChange: (value: string) => void;
    /** Quick percentage presets (goodwill: 20 / 50 / 100 %). */
    presets?: number[];
}

const CodeTermsFields = ({
    open,
    today,
    groupId,
    isPercentage,
    value,
    validUntil,
    onGroupIdChange,
    onIsPercentageChange,
    onValueChange,
    onValidUntilChange,
    presets,
}: Props) => {
    const { t } = useTranslation();
    // Bookable groups for the mapping select. A code targets one group, or —
    // via ALL_OBJECTS_SCOPE — none at all, which redemption reads as every
    // object of the tenant. Shared slice with the "Rental products" surface
    // (same GET /admin/group).
    const {
        data: groups,
        isLoading: groupsLoading,
        isUninitialized: groupsUninitialized,
        isError: groupsError,
    } = useGetAdminGroupsQuery(undefined, { skip: !open });
    const { services } = useTenantCapabilities();

    // Only groups whose use case this tenant offers are selectable — see the
    // helper for what this guard does and does not protect against.
    const selectableGroups = useMemo(
        () => selectableGroupsForServices(groups, services),
        [groups, services],
    );

    const typeToggle = (active: boolean, label: string, onClick: () => void) => (
        <button
            type={'button'}
            onClick={onClick}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                active
                    ? 'bg-primary text-text-buttonContrast'
                    : 'border border-foreground/15 text-foreground/60 hover:text-foreground'
            }`}
        >
            {label}
        </button>
    );

    return (
        <>
            <div>
                <LabelWithHelp
                    text={t('vouchers_codes.create_promo.group_label') + ' *'}
                    help={t('vouchers_codes.create_promo.help.group')}
                />
                <select
                    className={fieldClass}
                    value={groupId}
                    onChange={(e) => onGroupIdChange(e.target.value)}
                >
                    <option value={''} disabled>
                        {t('vouchers_codes.create_promo.group_placeholder')}
                    </option>
                    {/* Tenant-wide scope is unaffected by the use-case
                        guard below: it carries no group and no location, so
                        redemption can only ever match this tenant's own
                        objects — there is nothing foreign to offer. */}
                    <option value={ALL_OBJECTS_SCOPE}>
                        {t('vouchers_codes.create_promo.group_all_objects')}
                    </option>
                    {selectableGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                            {g.name ?? g.id.slice(0, 8)}
                        </option>
                    ))}
                </select>
                {groupId === ALL_OBJECTS_SCOPE && (
                    <p className={'mt-1 text-xs text-foreground/50'}>
                        {t('vouchers_codes.create_promo.group_all_objects_hint')}
                    </p>
                )}
                {/* A failed fetch is not "nothing configured" — say so
                    instead of blaming the tenant's use cases. */}
                {groupsError && (
                    <p className={'mt-1 text-xs text-foreground/50'}>
                        {t('vouchers_codes.create_promo.group_load_error')}
                    </p>
                )}
                {/* Only once the list has actually loaded — an empty
                    selection while the query is pending or not yet started
                    is not "nothing available". Irrelevant once the
                    tenant-wide scope is picked. */}
                {!groupsLoading
                    && !groupsUninitialized
                    && !groupsError
                    && selectableGroups.length === 0
                    && groupId !== ALL_OBJECTS_SCOPE && (
                    <p className={'mt-1 text-xs text-foreground/50'}>
                        {t('vouchers_codes.create_promo.group_empty')}
                    </p>
                )}
            </div>

            <div>
                <LabelWithHelp
                    text={t('vouchers_codes.create_promo.discount_type_label')}
                    help={t('vouchers_codes.create_promo.help.discount_type')}
                />
                <div className={'flex gap-2'}>
                    {typeToggle(
                        isPercentage,
                        t('vouchers_codes.create_promo.type_percentage'),
                        () => onIsPercentageChange(true),
                    )}
                    {typeToggle(
                        !isPercentage,
                        t('vouchers_codes.create_promo.type_fixed'),
                        () => onIsPercentageChange(false),
                    )}
                </div>
                {presets && isPercentage && (
                    <div className={'mt-2 flex gap-2'}>
                        {presets.map((preset) => (
                            <button
                                key={preset}
                                type={'button'}
                                onClick={() => onValueChange(String(preset))}
                                className={
                                    'rounded-md border border-foreground/15 px-3 py-1 text-xs ' +
                                    'font-medium text-foreground/70 hover:text-foreground'
                                }
                            >
                                {`${preset}%`}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <LabelWithHelp
                    text={t('vouchers_codes.create_promo.value_label') + ' *'}
                    help={t('vouchers_codes.create_promo.help.value')}
                />
                <div className={'relative'}>
                    <input
                        className={fieldClass}
                        type={'number'}
                        min={0}
                        max={isPercentage ? 100 : undefined}
                        value={value}
                        onChange={(e) => onValueChange(e.target.value)}
                    />
                    {isPercentage && (
                        <span
                            className={
                                'absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground/50'
                            }
                        >
                            %
                        </span>
                    )}
                </div>
            </div>

            <div>
                <LabelWithHelp
                    text={t('vouchers_codes.create_promo.expiry_label') + ' *'}
                    help={t('vouchers_codes.create_promo.help.expiry')}
                />
                <input
                    className={fieldClass}
                    type={'date'}
                    min={today}
                    value={validUntil}
                    onChange={(e) => onValidUntilChange(e.target.value)}
                />
            </div>
        </>
    );
};

export default CodeTermsFields;
